// What every finance query and mutation builds on: the keys under ["finance"], and the mutation that never retries,
// toasts its outcome and refetches all finance data. The pricing, ledger, expense, report, job and team invoice hooks share it.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { financeMutationOptions } from "../utils/mutations";

export const financeKeys = {
  all: ["finance"] as const,
  workTypes: () => [...financeKeys.all, "work-types"] as const,
  serviceLines: () => [...financeKeys.all, "service-lines"] as const,
  listini: () => [...financeKeys.all, "listini"] as const,
  clientContracts: (clientId: string) => [...financeKeys.all, "contracts", clientId] as const,
};

export interface FinanceMutationFlags {
  settleOnResponse?: boolean;
  refusedInline?: boolean;
}

/** Never retried. Settles after the finance data refetches, unless `settleOnResponse`, and toasts a refusal unless
 * `refusedInline` (see financeMutationOptions). */
export function useFinanceMutation<TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
  success?: (result: TResult) => string,
  { settleOnResponse = false, refusedInline = false }: FinanceMutationFlags = {},
) {
  const queryClient = useQueryClient();
  const addToast = useAppStore((state) => state.addToast);
  const refetch = () => queryClient.invalidateQueries({ queryKey: financeKeys.all });
  return useMutation({ mutationFn: run, ...financeMutationOptions({ toast: addToast, refetch }, success, settleOnResponse, refusedInline) });
}
